import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Navbar from './Navbar';
import Footer from './Footer';
import Container from './Container';
import Button from './Button';

const UpdatePasswordPage = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('success');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userName, setUserName] = useState('');

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '', color: '' };
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 10) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (score <= 2) return { score: 1, label: 'Αδύναμος', color: '#ef4444' };
    if (score <= 4) return { score: 2, label: 'Μέτριος', color: '#eab308' };
    return { score: 3, label: 'Ισχυρός', color: '#22c55e' };
  };

  const passwordStrength = getPasswordStrength(password);

  const EyeIcon = ({ show, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: 'absolute',
        right: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#9ca3af',
        padding: '0.25rem'
      }}
    >
      {show ? (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      ) : (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      )}
    </button>
  );

  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();

        if (error) {
          console.error('Auth check error:', error);
          return;
        }

        if (user) {
          const firstName = user.user_metadata?.first_name || '';
          const lastName = user.user_metadata?.last_name || '';
          if (firstName || lastName) {
            setUserName(`${firstName} ${lastName}`.trim());
          }
          if (import.meta.env.DEV) console.log('User authenticated:', user.email);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
      }
    };

    const timer = setTimeout(checkUser, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setModalMessage('Οι κωδικοί δεν ταιριάζουν');
      setModalType('error');
      setShowModal(true);
      return;
    }

    if (password.length < 6) {
      setModalMessage('Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες');
      setModalType('error');
      setShowModal(true);
      return;
    }

    setLoading(true);

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError || !sessionData.session) {
          setModalMessage('Δεν είστε συνδεδεμένοι. Παρακαλώ χρησιμοποιήστε ξανά τον σύνδεσμο πρόσκλησης από το email σας.');
          setModalType('error');
          setShowModal(true);
          setLoading(false);
          return;
        }
      }

      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      setModalMessage('Ο κωδικός σας ορίστηκε επιτυχώς! Θα μεταφερθείτε στον πίνακα ελέγχου.');
      setModalType('success');
      setShowModal(true);

      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Password update error:', error);
      setModalMessage(`Σφάλμα κατά τον ορισμό του κωδικού: ${error.message}`);
      setModalType('error');
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#1A202C', minHeight: '100vh' }}>
      <Navbar />

      <main style={{ padding: '4rem 0' }}>
        <Container>
          <div style={{
            maxWidth: '500px',
            margin: '0 auto'
          }}>
            {/* Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: '3rem'
            }}>
              <h1 style={{
                fontSize: '2.5rem',
                fontWeight: 'bold',
                color: '#f7fafc',
                marginBottom: '1rem'
              }}>
                Καλώς ήρθατε στο TailClinic
              </h1>
              {userName && (
                <p style={{
                  fontSize: '1.25rem',
                  color: '#10B981',
                  marginBottom: '1rem'
                }}>
                  {userName}
                </p>
              )}
              <p style={{
                fontSize: '1.125rem',
                color: '#a0aec0',
                lineHeight: '1.6',
                marginBottom: '1rem'
              }}>
                Ορίστε τον κωδικό πρόσβασής σας για να ολοκληρώσετε την εγγραφή σας.
              </p>
            </div>

            {/* Set Password Form */}
            <form onSubmit={handleSubmit} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2rem'
            }}>
              {/* New Password Field */}
              <div>
                <label style={{
                  display: 'block',
                  color: '#f7fafc',
                  fontSize: '1.125rem',
                  fontWeight: '500',
                  marginBottom: '0.75rem'
                }}>
                  Κωδικός Πρόσβασης
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Εισάγετε τον κωδικό σας"
                    required
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '1rem 1.25rem',
                      paddingRight: '3rem',
                      backgroundColor: '#2d3748',
                      border: '1px solid #4a5568',
                      borderRadius: '0.5rem',
                      color: '#f7fafc',
                      fontSize: '1.125rem',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      opacity: loading ? 0.6 : 1
                    }}
                    onFocus={(e) => {
                      if (!loading) {
                        e.target.style.borderColor = '#10B981';
                        e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#4a5568';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <EyeIcon show={showPassword} onClick={() => setShowPassword(!showPassword)} />
                </div>
                {password && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.25rem' }}>
                      {[1, 2, 3].map((level) => (
                        <div
                          key={level}
                          style={{
                            flex: 1,
                            height: '4px',
                            borderRadius: '2px',
                            backgroundColor: passwordStrength.score >= level ? passwordStrength.color : '#4a5568',
                            transition: 'background-color 0.3s ease'
                          }}
                        />
                      ))}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label style={{
                  display: 'block',
                  color: '#f7fafc',
                  fontSize: '1.125rem',
                  fontWeight: '500',
                  marginBottom: '0.75rem'
                }}>
                  Επιβεβαίωση Κωδικού
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Επαναλάβετε τον κωδικό σας"
                    required
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '1rem 1.25rem',
                      paddingRight: '3rem',
                      backgroundColor: '#2d3748',
                      border: '1px solid #4a5568',
                      borderRadius: '0.5rem',
                      color: '#f7fafc',
                      fontSize: '1.125rem',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      opacity: loading ? 0.6 : 1
                    }}
                    onFocus={(e) => {
                      if (!loading) {
                        e.target.style.borderColor = '#10B981';
                        e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                      }
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#4a5568';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <EyeIcon show={showConfirmPassword} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />
                </div>
                {password && confirmPassword && password !== confirmPassword && (
                  <p style={{ color: '#f56565', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    Οι κωδικοί δεν ταιριάζουν
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div style={{ marginTop: '1.5rem' }}>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  style={{ width: '100%' }}
                  disabled={loading}
                >
                  {loading ? 'Αποθήκευση...' : 'Ορισμός Κωδικού'}
                </Button>
              </div>
            </form>
          </div>
        </Container>
      </main>

      <Footer />

      {/* Success/Error Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md" style={{
            backgroundColor: '#2D3748',
            borderColor: '#4A5568'
          }}>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white">
                  {modalType === 'success' ? 'Επιτυχία' : 'Σφάλμα'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-300"
                >
                  <span className="sr-only">Κλείσιμο</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <div className="text-sm text-gray-300 whitespace-pre-line">
                  {modalMessage}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                  style={{ backgroundColor: '#10B981' }}
                >
                  Εντάξει
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdatePasswordPage;
